import React, { useEffect, useState, useMemo } from 'react';
import { createRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { Route as rootRoute } from '../../__root';
import { useLiveStreamsStore } from '../../../store/liveStreamsStore';
import { useClientsStore } from '../../../store/clientsStore';
import { Layout } from '@/client/layouts/layout';
import type { LiveStreamTableRow, LiveStreamWithEventResponse, WebSocketEventData } from '@shared/types';
import { generateStreamingURIs } from '@/client/lib/format';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  flexRender,
} from '@tanstack/react-table';
import { createLiveStreamsColumns, StatusBadge } from '@/client/components/tables/live-streams-columns';
import { useViewerCounts } from '@/client/hooks/useViewerCounts';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { Button } from '@/client/components/ui/button';
import { Input } from '@/client/components/ui/input';
import { RefreshCw } from 'lucide-react';

// Transform WebSocket event data to table row format
function wsEventToTableRow(
  event: WebSocketEventData,
  clientNameMap: Map<string, string>,
  clientTimeZoneMap: Map<string, string | null>
): LiveStreamTableRow {
  return {
    // Core identifiers
    clientID: event.clientID,
    clientName: clientNameMap.get(event.clientID) || null,
    clientTimeZone: clientTimeZoneMap.get(event.clientID) || null,
    eventID: event.eventID,
    eventTitle: event.title,
    liveID: event.liveStats?.liveID || null,
    status: event.eventStatus,
    streamStatus: event.liveStats?.status || null,

    // Schedule
    eventStartDateTime: event.startDateTime,
    eventEndDateTime: null, // Not available in WebSocket data
    startDateTime: event.liveStats?.startDate || null,
    endDateTime: null,
    autoStart: event.autoStart,
    autoStop: event.autoStop,
    preroll: event.preroll,

    // Encoder & Infrastructure
    encoderID: event.assignedEncoder,
    ingInstanceID: null, // Not available in WebSocket data
    egInstanceID: null,
    wcStreamID: null,

    // URIs
    streamingURIs: event.streamingURIs ? {
      main: event.streamingURIs.main || null,
      backup: event.streamingURIs.backup || null,
    } : null,

    // Metadata
    customID: event.customID,
    description: event.description,
    notes: event.notes,
    private: event.private,
    liveToBreak: event.liveToBreak,

    // Live stats
    liveStats: event.liveStats ? {
      runtime: event.liveStats.runtime,
      status: event.liveStats.status,
    } : null,
  };
}

// Transform API response to table row format
function apiEventToTableRow(event: LiveStreamWithEventResponse): LiveStreamTableRow {
  return {
    // Core identifiers
    clientID: event.clientID,
    clientName: event.clientName,
    clientTimeZone: event.clientTimeZone,
    eventID: event.eventID,
    eventTitle: event.eventTitle,
    liveID: event.liveID,
    status: event.status,
    streamStatus: event.streamStatus,

    // Schedule
    eventStartDateTime: event.eventStartDateTime,
    eventEndDateTime: event.eventEndDateTime,
    startDateTime: event.startDateTime,
    endDateTime: event.endDateTime,
    autoStart: event.autoStart,
    autoStop: event.autoStop,
    preroll: event.preroll,

    // Encoder & Infrastructure
    encoderID: event.encoderID,
    ingInstanceID: event.ingInstanceID,
    egInstanceID: event.egInstanceID,
    wcStreamID: event.wcStreamID,

    // URIs - generated from clientID/eventID pattern
    streamingURIs: generateStreamingURIs(event.clientID, event.eventID),

    // Metadata
    customID: event.customID,
    description: event.description,
    notes: event.notes,
    private: event.private,
    liveToBreak: event.liveToBreak,

    // Live stats - not available from API
    liveStats: null,
  };
}

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'events/live-streams',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: LiveStreamsComponent,
});

function LiveStreamsComponent() {
  const { getAccessTokenSilently, logout } = useAuth0();
  // Select each field individually to ensure reactivity
  const liveEvents = useLiveStreamsStore((state) => state.liveEvents);
  const completedEvents = useLiveStreamsStore((state) => state.completedEvents);
  const liveStreams = useLiveStreamsStore((state) => state.liveStreams); // Legacy API data
  const loading = useLiveStreamsStore((state) => state.loading);
  const error = useLiveStreamsStore((state) => state.error);
  const fetchLiveStreams = useLiveStreamsStore((state) => state.fetchLiveStreams);
  const eventMap = useLiveStreamsStore((state) => state.eventMap);

  // Get all clients for name lookup
  const clients = useClientsStore((state) => state.clients);

  const navigate = useNavigate();

  // Navigate to stream detail, normalizing eventID to clientID_eventID format
  const navigateToStream = (stream: LiveStreamTableRow) => {
    const eventID = stream.eventID!;
    const streamId = eventID.includes('_') ? eventID : `${stream.clientID}_${eventID}`;
    navigate({ to: '/events/$eventId', params: { eventId: streamId } });
  };

  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [refreshCooldown, setRefreshCooldown] = useState(false);

  // Merge WebSocket and API data intelligently
  // WebSocket data takes precedence; only show API events that haven't been updated via WebSocket yet
  const displayData = useMemo(() => {
    if (liveEvents.length === 0 && completedEvents.length === 0) {
      // No WebSocket data yet, use API data
      return liveStreams;
    }

    // Build set of all WebSocket-tracked event IDs
    // Use eventMap (persists even after clearing completedEvents) to track known events
    const wsLiveEventIDs = new Set(liveEvents.map(e => e.eventID));
    const allWsEventIDs = new Set([...wsLiveEventIDs, ...eventMap.keys()]);

    // Start with all WebSocket LIVE events only (not completed)
    const merged = [...liveEvents];

    // Add API events ONLY if we haven't received ANY WebSocket update for them
    // (including completed events - we don't want to show those in the live list)
    // NOTE: API returns eventID as "clientID_eventID", WebSocket returns just "eventID"
    // We need to extract and compare the eventID part only
    for (const apiEvent of liveStreams) {
      // Extract just the eventID part (after the underscore) from API data
      const apiEventIDPart = apiEvent.eventID?.includes('_')
        ? apiEvent.eventID.split('_')[1]
        : apiEvent.eventID;

      const isTrackedByWebSocket = allWsEventIDs.has(apiEventIDPart || '');

      if (apiEvent.eventID && !isTrackedByWebSocket) {
        merged.push(apiEvent as any);
      }
    }

    return merged;
  }, [liveEvents, completedEvents, liveStreams, eventMap]);

  const loadLiveStreams = async () => {
    try {
      const token = await getAccessTokenSilently();
      await fetchLiveStreams(token, logout);
    } catch (err) {
      console.error('Error fetching live streams:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshCooldown(true);
    await loadLiveStreams();
    setTimeout(() => setRefreshCooldown(false), 5000);
  };

  useEffect(() => {
    loadLiveStreams();
  }, [getAccessTokenSilently]);

  // Build client name lookup map (used by multiple transforms)
  const clientNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const client of clients) {
      if (client.clientID && client.name) {
        map.set(client.clientID, client.name);
      }
    }
    return map;
  }, [clients]);

  // Build client timezone lookup map
  const clientTimeZoneMap = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const client of clients) {
      if (client.clientID) {
        map.set(client.clientID, client.timeZone);
      }
    }
    return map;
  }, [clients]);

  // Transform events to match table structure
  // Works with both WebSocket events and API LiveStream objects
  const transformedEvents = useMemo((): LiveStreamTableRow[] => {
    return displayData.map((event): LiveStreamTableRow => {
      // Check if this is WebSocket data (has 'title' and 'eventStatus')
      // or API data (has 'eventTitle' and 'status')
      if ('title' in event || 'eventStatus' in event) {
        return wsEventToTableRow(event as WebSocketEventData, clientNameMap, clientTimeZoneMap);
      } else {
        return apiEventToTableRow(event as LiveStreamWithEventResponse);
      }
    });
  }, [displayData, clientNameMap, clientTimeZoneMap]);

  const filteredLiveEvents = useMemo(() => {
    if (!searchFilter) return transformedEvents;
    return transformedEvents.filter((event) =>
      event.eventTitle?.toLowerCase().includes(searchFilter.toLowerCase()) ||
      event.clientID?.toLowerCase().includes(searchFilter.toLowerCase())
    );
  }, [transformedEvents, searchFilter]);

  // Transform completed events to table row format
  const transformedCompletedEvents = useMemo((): LiveStreamTableRow[] => {
    return completedEvents.map(event => wsEventToTableRow(event, clientNameMap, clientTimeZoneMap));
  }, [completedEvents, clientNameMap, clientTimeZoneMap]);

  // Fetch viewer counts for all displayed live events
  const {
    viewerCounts,
    totalViewers,
    loading: viewerCountsLoading,
    available: viewerCountsAvailable,
    lastUpdated: viewerCountsLastUpdated,
  // Viewer counts are pre-warmed by server-side poller (src/server/analytics/poller.ts)
  } = useViewerCounts(filteredLiveEvents, { enablePolling: true });

  // Create columns with viewer counts data
  const columns = useMemo(
    () => createLiveStreamsColumns(viewerCounts, viewerCountsLoading, viewerCountsAvailable),
    [viewerCounts, viewerCountsLoading, viewerCountsAvailable]
  );

  const table = useReactTable({
    data: filteredLiveEvents,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: { sorting },
  });

  return (
    <Layout>
      <div>
        <section>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <h3 className="text-2xl font-semibold">Live Streams</h3>
              {viewerCountsAvailable && totalViewers > 0 && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
                  {totalViewers.toLocaleString()} total viewers
                </span>
              )}
            </div>
            <p className="font-mono text-sm text-muted-foreground">Live data</p>
          </div>

          <div className="flex justify-end items-center gap-4 mb-4">
            <Input
              placeholder="Filter by Event name..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="max-w-xs"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading || refreshCooldown}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Force Refresh
            </Button>
          </div>

          {loading && <p>Loading live streams...</p>}

          {error && (
            <div className="p-4 bg-red-100 border-l-4 border-red-600 rounded mt-4">
              <p className="m-0 text-red-800 font-medium">Error: {error}</p>
            </div>
          )}

          {!loading && !error && displayData.length === 0 && (
            <p>No live events found.</p>
          )}

          {!loading && !error && displayData.length > 0 && (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => navigateToStream(row.original)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>

        {/* Completed Streams Section */}
        {transformedCompletedEvents.length > 0 && (
          <section className="mt-8">
            <div className="mb-4">
              <h3 className="text-xl font-semibold">Completed Streams</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Events that recently finished streaming. These can go live again.
              </p>
            </div>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Client</TableHead>
                    <TableHead className="text-xs">Event Title</TableHead>
                    <TableHead className="text-xs">Started At</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transformedCompletedEvents.map((event) => (
                    <TableRow
                      key={event.eventID}
                      className="cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => navigateToStream(event)}
                    >
                      <TableCell className="text-xs">{event.clientName || '-'}</TableCell>
                      <TableCell className="text-xs max-w-[300px] truncate">{event.eventTitle || '-'}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {event.startDateTime
                          ? new Date(event.startDateTime).toLocaleString()
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={event.status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>
        )}

      </div>
    </Layout>
  );
}
